import { Project, Experience, Education, Skill, Language, Interest } from '../types';

export const projects: Project[] = [
    {
        id: '1',
        title: 'Interactive 3D Bathroom Planner',
        description: "Academic project (in cooperation with New Living Design GmbH) focused on developing a browser-based 3D bathroom planner with AI-supported consulting and automated quotation generation for the digitalization of the consulting and sales process. " +
            "Joint responsibility for the design, technical implementation, and evaluation of the prototype in a team, as well as close coordination with the industry partner. " +
            "Iterative development, 3D web technologies (three.js/WebGL), AI integration, prototyping, and usability testing were key aspects of the project. " +
            "The result was a functional web prototype with interactive 3D planning, AI support, and automated quotation logic, receiving positive feedback from industry partners and lecturers.",
        longDescription: 'Development of a browser-based 3D bathroom planner with AI-supported consulting and automated quotation generation for the digitalization of the consulting and sales process. Joint responsibility for the design, technical implementation, and evaluation of the prototype in a team, as well as close coordination with the industry partner.',
        technologies: ['three.js', 'WebGL', 'AI Integration', 'JavaScript', 'Prototyping'],
        images: ['/nld_logo.jpg'],
        githubUrl: 'https://github.com/MichalVerdile/bathforge',
        featured: true,
        category: 'web',
        completedDate: '2025-12-15'
    },
    {
        id: '2',
        title: 'Quantum Machine-Learning',
        description: "Academic project investigating whether randomness from quantum mechanical decoherence has statistical similarities with biological stochastics (e.g., gene expression). " +
            "Co-author responsibilities included study design, implementation of quantum machine learning models, and statistical analysis of results. " +
            "Variational Quantum Classifier (VQC), simulation of quantum noise, biological stochastic models, and statistical analyses (KS test, entropy, autocorrelation) were implemented. " +
            "The study revealed high statistical agreement between quantum mechanical and biological randomness patterns (78.5% similarity), indicating common mathematical properties of randomness.",
        longDescription: 'Investigation of whether randomness from quantum mechanical decoherence has statistical similarities with biological stochastics. Co-author; study design, implementation of quantum machine learning models, and statistical analysis of results. High statistical agreement between quantum mechanical and biological randomness patterns (78.5% similarity) was achieved.',
        technologies: ['Python', 'Quantum Computing', 'VQC', 'Machine Learning', 'Statistical Analysis'],
        images: ['/pennylane-datasets-hero-generic-2.png'],
        githubUrl: 'https://github.com/MichalVerdile/xkye_verdiant_qc_machine_learning',
        featured: true,
        category: 'other',
        completedDate: '2025-12-15'
    },
    {
        id: '3',
        title: 'Lernello - AI E-Learning Platform',
        description: "Academic project (in cooperation with Wildarmaturen AG) focused on developing a web platform (Lernello) that uses AI to analyze uploaded files and automatically generate e-learnings with quizzes and summaries. " +
            "Responsibilities included frontend and backend development, integration of an H2 database, linting, and structured code style. " +
            "Dynamic AI prompts were designed to adapt content across different fields of study. " +
            "The project involved continuous collaboration with the partner company to incorporate customer requirements, as well as teamwork with seven other members. " +
            "The result was a fully functional platform, successfully presented with positive feedback from lecturers and company representatives. ",
        longDescription: 'Developed in collaboration with Wildarmaturen AG and a team of six students. Lernello uses artificial intelligence to analyze uploaded documents and automatically creates comprehensive e-learning modules including quizzes and summaries. Features dynamic AI prompts adaptable to different academic fields and continuous integration of client feedback.',
        technologies: ['SvelteKit', 'Spring Boot', 'H2', 'JSON', 'HTML', 'CSS'],
        images: ['/lernello.png'],
        githubUrl: 'https://github.com/nova-omnia/lernello',
        featured: true,
        category: 'web',
        completedDate: '2025-06-15'
    },
    {
        id: '4',
        title: 'Real Estate Price Predictor',
        description: "Academic international cooperation project with Grand Valley State University students (GVSU, USA), focused on developing an AI-powered system for predicting real estate prices based on structured data, text descriptions, and image material. " +
            "Responsibilities included data preprocessing, model training, and backend integration. " +
            "A comprehensive feature-engineering pipeline was built, including clustering, image feature extraction, geographic features, and one-hot encoding. " +
            "Multiple regression models were implemented with hyperparameter tuning, and an interactive frontend for price queries was integrated. " +
            "The project involved continuous collaboration with GVSU students to align architecture, features, and presentation. " +
            "The result was a fully functional real estate price predictor with an interactive web interface, receiving positive feedback from lecturers and project partners. ",
        longDescription: 'International cooperation project with Grand Valley State University. This AI system predicts real estate prices by analyzing multiple data sources including structured data, property descriptions, and images. Features comprehensive data preprocessing, multiple regression models with hyperparameter tuning, and an interactive frontend for price queries.',
        technologies: ['Python', 'React', 'ResNet', 'PostgreSQL', 'Docker', 'Conda', 'Scraping Tools'],
        images: ['/real_estate_price_predictor.jpg'],
        githubUrl: 'https://github.com/daai25/real_estate_price_predictor',
        featured: true,
        category: 'web',
        completedDate: '2025-05-20'
    },
    {
        id: '5',
        title: 'New Living Design Website',
        description: "Professional project at New Living Design GmbH, involving the relaunch and technical redevelopment of the company website to provide a contemporary presentation of interior design and bathroom solutions. " +
            "Responsibilities included concept development, layout, and technical implementation. " +
            "The site was optimized for responsive design to ensure optimal functionality across desktop, tablet, and smartphone. " +
            "Additional work included SEO and performance improvements such as load time optimization, image compression, and caching. " +
            "Regular coordination with the client ensured continuous integration of design requests, updates, and content. " +
            "The result was an aesthetically appealing website for New Living Design with a clear presentation of their offerings, receiving positive feedback from both management and clients regarding visual quality and performance. ",
        longDescription: 'Complete technical relaunch of New Living Design GmbH corporate website. Focused on modern presentation of interior architecture and bathroom solutions with responsive design optimization for all devices. Implemented comprehensive SEO and performance optimizations including load time improvements, image compression, and caching strategies.',
        technologies: ['HTML', 'CSS', 'JavaScript', 'React', 'SEO Tools'],
        images: ['/nld_logo.jpg'],
        liveUrl: 'https://newlivingdesign.ch',
        featured: false,
        category: 'web',
        completedDate: '2025-03-10'
    },
    {
        id: '6',
        title: 'VR Architecture Sustainability',
        description: "Academic project in collaboration with Openly AG, focused on developing innovative VR concepts in the context of architecture and sustainable building, using the Design Thinking methodology. " +
            "Responsibilities included idea development and prototyping within the team, applying the Design Thinking process to explore how Virtual Reality can make sustainable architecture tangible and understandable. " +
            "The project involved close collaboration with Openly through continuous exchange. " +
            "The result was a validated VR prototype that makes architecture and sustainability interactively experienceable, receiving positive feedback from Openly and lecturers. ",
        longDescription: 'School project in collaboration with Openly AG using Design Thinking methodology. Developed innovative VR concepts to make sustainable architecture experiential and understandable. The project explored how Virtual Reality can be utilized to make sustainable building practices more accessible and engaging for users.',
        technologies: ['Design Thinking', 'VR (Delightex)', 'Prototyping (Miro)'],
        images: ['/vr_architecture.jpg'],
        liveUrl: 'https://edu.delightex.com/JUM-UWA',
        featured: false,
        category: 'other',
        completedDate: '2025-01-15'
    },
    {
        id: '7',
        title: 'Alias ZHAW Website Redesign',
        description: "Professional project at Alias – Students of ZHAW, involving a complete rebuild and redesign of the Alias website to provide a modern and user-friendly presentation of the student association. " +
            "Responsibilities included defining structure, content, and UI/UX design, integrating and maintaining member management, and building an e-shop for merchandise products. " +
            "A consistent and appealing corporate design was created, with continuous adjustments based on feedback from the association. " +
            "The result was a functional, modernized website with an integrated e-shop, receiving positive feedback from both members and the board. ",
        longDescription: 'Professional project for Alias - ZHAW student association. Complete website redesign featuring modern UI/UX design, integrated member management system, and e-commerce functionality for merchandise sales. Developed consistent corporate design and implemented continuous improvements based on member feedback.',
        technologies: ['WordPress', 'HTML', 'CSS', 'Woo-Commerce', 'SEO Tools'],
        images: ['/alias_logo.png'],
        liveUrl: 'https://alias-zhaw.ch',
        featured: false,
        category: 'web',
        completedDate: '2024-12-15'
    },
    {
        id: '8',
        title: 'AI Quiz Generator (Java)',
        description: "Academic project focused on developing an AI-based system that analyzes uploaded files to automatically generate quizzes and summaries. " +
            "Responsibilities included frontend and backend development, as well as integrating AI prompts for dynamic adaptation across different fields of study. " +
            "The project was carried out in collaboration with five other team members. " +
            "Key achievements included building dynamic AI prompt-linking applicable across disciplines, delivering a fully functional application that received positive feedback from lecturers. ",
        longDescription: 'School project developed with a team of six members. This AI-based system analyzes uploaded documents to automatically generate quiz questions and summaries. Features dynamic AI prompt integration adaptable to various academic fields and a user-friendly JavaFX interface. Future plans include migration to a React-based web application.',
        technologies: ['Java', 'JavaFX', 'JSON', 'HTML'],
        images: ['/StudyIT_Logo.png'],
        featured: false,
        category: 'desktop',
        completedDate: '2024-05-20'
    },
    {
        id: '9',
        title: 'Dynamic Grid Searcher',
        description: "Development of an application that automatically generates filters from each column of a database table, enabling dynamic data filtering. " +
            "Primary responsibility for implementing the frontend and backend. " +
            "Developed server-side classes and UI components. " +
            "Created unit tests in and end-to-end tests. " +
            "Adapted SQL scripts for database joins and feature extensions.",
        longDescription: 'Developed as part of my IPA (Interdisciplinary Practical Work) at Swiss National Bank. This application automatically creates filter interfaces from database columns, enabling users to dynamically filter and display large datasets. The main challenge was efficiently displaying massive amounts of data, solved through intelligent filter logic that only loads filtered data during page loads.',
        technologies: ['TypeScript', 'C#', 'SQL', 'Cypress'],
        images: ['/dynamic_grid.jpg'],
        featured: false,
        category: 'web',
        completedDate: '2022-08-15'
    }
];

export const experiences: Experience[] = [
    {
        id: '1',
        company: 'ALIAS ZHAW Student Association',
        position: 'Website Optimization (20%)',
        startDate: '2024-08-01',
        endDate: undefined,
        isCurrentJob: true,
        description: [
            'Ongoing website optimization and maintenance for ZHAW student association',
            'Performance improvements and user experience enhancements',
            'Technical support and troubleshooting for existing web platform',
            'Collaboration with association members to implement requested features'
        ],
        technologies: ['WordPress', 'HTML', 'CSS', 'Performance Optimization', 'Web Maintenance']
    },
    {
        id: '2',
        company: 'Swiss National Bank (SNB)',
        position: 'Full Stack Developer (60%)',
        startDate: '2022-08-01',
        endDate: '2024-07-31',
        isCurrentJob: false,
        description: [
            'Maintenance and extension of web platform applications',
            'Development and implementation of data management functions',
            'Creation of various proof-of-concepts to validate new ideas',
            'Extension and maintenance of end-to-end tests for quality assurance',
            'Optimization and further development of reusable search components'
        ],
        technologies: ['TypeScript', 'C#', '.NET', 'SQL', 'Angular', 'End-to-End Testing']
    },
    {
        id: '3',
        company: 'Swiss National Bank (SNB)',
        position: 'IMS Internship (100%)',
        startDate: '2021-08-01',
        endDate: '2022-07-31',
        isCurrentJob: false,
        description: [
            'Collaboration in building and operating web platforms',
            'Development of reusable component for keyword search functionality',
            'Implementation of application for data search in dynamic grids',
            'Extension and maintenance of end-to-end tests for quality assurance',
            'Application of modern technologies including Angular, Microsoft .NET and SQL'
        ],
        technologies: ['Angular', 'Microsoft .NET', 'SQL', 'End-to-End Testing', 'Component Development']
    },
    {
        id: '4',
        company: 'ALIAS ZHAW',
        position: 'Section Treasurer',
        startDate: '2023-09-01',
        endDate: undefined,
        isCurrentJob: true,
        description: [
            'Financial management and budgeting for ZHAW student association',
            'Maintaining financial records and expense tracking',
            'Coordinating with board members on financial matters',
            'Managing event budgets and financial planning'
        ],
        technologies: ['Financial Management', 'Excel', 'Budgeting', 'Administration']
    },
    {
        id: '5',
        company: 'Private Tutoring',
        position: 'Mathematics & Science Tutor',
        startDate: '2020-05-01',
        endDate: undefined,
        isCurrentJob: true,
        description: [
            'Providing tutoring services for secondary school students',
            'Teaching mathematics, physics, and computer science concepts',
            'Developing personalized learning strategies for individual students',
            'Helping students improve academic performance and understanding'
        ],
        technologies: ['Teaching', 'Mathematics', 'Physics', 'Computer Science', 'Pedagogy']
    },
    {
        id: '6',
        company: 'JEM Ceramik GmbH',
        position: 'Accounting Assistant',
        startDate: '2022-01-01',
        endDate: '2022-02-28',
        isCurrentJob: false,
        description: [
            'Temporary position supporting company accounting operations',
            'Data entry and financial record maintenance',
            'Invoice processing and administrative support',
            'Gained practical experience in business accounting processes'
        ],
        technologies: ['Accounting Software', 'Excel', 'Data Entry', 'Invoice Management']
    },
    {
        id: '7',
        company: 'IMS Class Administration',
        position: 'Class Treasurer',
        startDate: '2018-08-01',
        endDate: '2022-07-31',
        isCurrentJob: false,
        description: [
            'Financial management for IMS class activities and events',
            'Collecting and managing class funds for various activities',
            'Organizing financial aspects of class trips and events',
            'Maintaining transparent financial records for classmates'
        ],
        technologies: ['Financial Management', 'Excel', 'Event Planning', 'Administration']
    }
];

export const education: Education[] = [
    {
        id: '1',
        institution: 'ZHAW Zurich University of Applied Sciences',
        degree: 'Bachelor of Science',
        field: 'Computer Science',
        startDate: '2022-09-01',
        endDate: undefined,
        isCurrentlyStudying: true,
        description: [
            'Solid university education in computer science with a focus on software engineering, data processing, and modern AI methods',
            'Practical studies with project, research, and team work',
            'Bachelor\'s thesis (in preparation):',
            'Quantum-informed neural networks for molecular structure analysis – development and evaluation of a physics-informed AI model that combines classical deep learning approaches with quantum mechanical and domain-specific constraints to make chemical predictions more robust and plausible.'
        ]
    },
    {
        id: '2',
        institution: 'Alte Kantonsschule Aarau',
        degree: 'Informatikmittelschule (IMS)',
        field: 'Computer Science',
        startDate: '2018-08-01',
        endDate: '2022-07-31',
        isCurrentlyStudying: false,
        description: [
            'Solid education in computer science and economics',
            'Focus areas: Programming, data processing, software development, project work',
            'Combination of theoretical knowledge and practical computer science projects',
            'Thesis topics:',
            'Big data and digital disruption – Analysis of the impact of data-driven technologies on the consumer goods industry and commodity trading (interdisciplinary project work)',
            'DynamicGrid-Searcher MiniApp for InfoCenterX – Development of a web-based search and grid component (interdisciplinary practical work)'
        ]
    }
];

export const skills: Skill[] = [
    // Frontend
    { id: '1', name: 'TypeScript', level: 'expert', category: 'frontend' },
    { id: '2', name: 'React', level: 'advanced', category: 'frontend' },
    { id: '3', name: 'JavaScript', level: 'expert', category: 'frontend' },
    { id: '4', name: 'HTML/CSS', level: 'expert', category: 'frontend' },
    { id: '5', name: 'SvelteKit', level: 'advanced', category: 'frontend' },
    { id: '6', name: 'JavaFX', level: 'intermediate', category: 'frontend' },
    { id: '7', name: 'WordPress', level: 'advanced', category: 'frontend' },

    // Backend
    { id: '8', name: 'C#', level: 'expert', category: 'backend' },
    { id: '9', name: 'Java', level: 'advanced', category: 'backend' },
    { id: '10', name: 'Spring Boot', level: 'advanced', category: 'backend' },
    { id: '11', name: 'Python', level: 'advanced', category: 'backend' },
    { id: '12', name: 'REST APIs', level: 'expert', category: 'backend' },
    { id: '13', name: 'ResNet/AI', level: 'intermediate', category: 'backend' },

    // Database
    { id: '14', name: 'SQL', level: 'expert', category: 'database' },
    { id: '15', name: 'PostgreSQL', level: 'advanced', category: 'database' },
    { id: '16', name: 'H2 Database', level: 'advanced', category: 'database' },
    { id: '17', name: 'Database Design', level: 'advanced', category: 'database' },

    // Tools
    { id: '18', name: 'Git', level: 'expert', category: 'tools' },
    { id: '19', name: 'Docker', level: 'intermediate', category: 'tools' },
    { id: '20', name: 'Cypress', level: 'advanced', category: 'tools' },
    { id: '21', name: 'Delightex VR', level: 'intermediate', category: 'tools' },
    { id: '22', name: 'Miro', level: 'intermediate', category: 'tools' },
    { id: '23', name: 'SEO Tools', level: 'advanced', category: 'tools' },
    { id: '24', name: 'Design Thinking', level: 'advanced', category: 'frontend' },
    { id: '25', name: 'UI/UX Design', level: 'advanced', category: 'frontend' },
    { id: '26', name: 'Performance Optimization', level: 'advanced', category: 'backend' }
];

export const languages: Language[] = [
    {
        id: '1',
        name: 'German',
        level: 'Native',
        description: 'Native speaker'
    },
    {
        id: '2',
        name: 'Italian',
        level: 'Native',
        description: 'Native speaker'
    },
    {
        id: '3',
        name: 'English',
        level: 'B2',
        description: 'Upper intermediate proficiency'
    },
    {
        id: '4',
        name: 'French',
        level: 'B2',
        description: 'Upper intermediate proficiency'
    }
];

export const interests: Interest[] = [
    {
        id: '1',
        name: 'Cooking',
        description: 'Passionate about exploring different cuisines and cooking techniques'
    },
    {
        id: '2',
        name: 'Saxophone',
        description: 'Playing saxophone as a creative outlet and musical expression'
    },
    {
        id: '3',
        name: 'Travel',
        description: 'Exploring new cultures, places, and gaining diverse experiences'
    }
];